import * as Either from 'fp-ts/Either';
import { formatUnits } from '@ethersproject/units';
import { FC, useCallback, useMemo } from 'react';
import { BIG_ZERO } from '../../../constants';
import useNotify from '../../../hooks/useNotify';
import useSiloRouter from '../../../hooks/useSiloRouter';
import {
  ActionForm,
  DoSubmit,
  LiquidityTooltip,
  SubmitButtonContent,
  UnstableOracleWarning,
  useActionFormContext,
  ValidateForm,
} from '../common/ActionForm';
import {
  disjoinSymbols,
  getCollateralAssets,
  getHasEffectiveCollateral,
  getNonDebtAssets,
  getAvailableCollateralAssets,
  getUserHasLoan,
  Recipe,
} from 'queries/decoratedSilo';
import { useThisDecoratedSilo } from '../silo';
import { useRouterSelectedAddress } from '../common/ActionForm/useRouterSelectedAddress';
import { useClearAssetFromQueryString } from '../silo/hooks';
import { GetMaxAmount, MaxAmount } from '../../../hooks/useMaxTokenAmount';
import { BigNumber } from '@ethersproject/bignumber';
import { calculateLTV, mapToCollateralAsset, mapToDebtAsset } from 'app/features/util/ltv';
import { HealthFactor, HealthFactorConfig } from '../common/HealthFactor';
import { pipe } from 'fp-ts/lib/function';
import { useSiloConfiguration } from '@silo/config-legacy';
import { filter } from 'fp-ts/lib/Array';
import { GetAverageAPY, MapAPYData } from '../common/ActionForm/ActionFormCharts';
import { calculateUtilization, convertCurrency } from 'app/functions/silo/calculations';
import mean from 'lodash/mean';
import { GetNewUtilization } from '../../../hooks/useUtilizationImpact';
import posthog from 'posthog-js';
import { AssetBorrowInfo, UserBorrowInfo } from '../common/ActionForm/BorrowInfo';
import {
  adjustToFullUSD,
  asDecimal,
  asFullUSD,
  handleParsedError,
  parseUserInitiatedTransactionError,
} from '@silo/utils';
import { useAddTransaction } from '@silo/web3';
import { useFinishTransaction } from '../silo/useFinishTransaction';
import { useIsAssetActionFrozen } from '../common/ActionForm/useIsFrozen';
import { getAssetsToDisplay } from './getAssetsToDisplay';
import { getMinBorrowAmount } from 'app/config/networks';
import { LiquidationPriceSimulator } from './LiquidationPriceSimulator';
import { getNewLTV } from './getNewLtv';
import { BigNumberWithDecimals, ProtocolInstance, makeAddressSync } from '@silo/types';
import { getHasUnstableOracle } from '@silo/config';

export type DoBorrow = DoSubmit;

export const useDoBorrow = (): DoBorrow => {
  const { siloAddress } = useThisDecoratedSilo();
  const { routerBorrow } = useSiloRouter();
  const addTransaction = useAddTransaction();
  const { notifyError, notifyWarning } = useNotify();
  const finishTransaction = useFinishTransaction();

  return useCallback(
    async ({ asset, amount, isFullAmount }) => {
      try {
        const tx = await routerBorrow(siloAddress, asset.assetAddress, amount);

        const formattedAmount = asDecimal(formatUnits(amount, asset.decimals));
        addTransaction(tx, `Borrow ${formattedAmount} ${asset.symbol}`);
        posthog.capture('borrow', { siloAddress, assetAddress: asset.assetAddress, amount });

        const { status } = await tx.wait();

        if (status === 0) {
          throw new Error(`Transaction ${tx.hash} reverted`);
        }

        finishTransaction(getBorrowRecipe({ asset, amount, isFullAmount }));
      } catch (err) {
        console.error('Error on borrow:', err);
        const parsed = parseUserInitiatedTransactionError(err);

        const captureAndNotify = (message: string) => {
          notifyError(message);
        };

        const { rethrow } = handleParsedError({
          onWarning: notifyWarning,
          onError: captureAndNotify,
          onUnparsed: captureAndNotify,
        })(parsed);

        // re-throw error to be handled by react-query
        if (rethrow) throw err;
      }
    },
    [routerBorrow, siloAddress, addTransaction, notifyWarning, notifyError, finishTransaction]
  );
};

type GetBorrowRecipe = (...params: Parameters<DoBorrow>) => Recipe;

const getBorrowRecipe: GetBorrowRecipe =
  ({ asset, amount }) =>
  (draft) => {
    const assetToUpdate = draft.assets.find(({ assetAddress }) => asset.assetAddress === assetAddress);

    if (!assetToUpdate) return;

    const {
      totalBorrowAmount: previousTotalBorrowAmount,
      debtBalance: previousDebtBalance,
      userBalance: previousUserBalance,
      projectedDebtBalance: previousProjectedDebtBalance,
    } = assetToUpdate;

    assetToUpdate.totalBorrowAmount = previousTotalBorrowAmount.add(amount);
    assetToUpdate.debtBalance = previousDebtBalance.add(amount);
    // just adding for now - could optimize by adding a proportional amount of interest
    assetToUpdate.projectedDebtBalance = previousProjectedDebtBalance.add(amount);
    assetToUpdate.userBalance = previousUserBalance.add(amount);
  };

const getMaxAmount: GetMaxAmount = ({ currentLTV, selectedAsset, decoratedSilo }) => {
  const getUserMax = (): MaxAmount => {
    const { assetAddress, decimals } = selectedAsset;

    if (currentLTV) {
      const debtAssetMetrics = currentLTV.debtAssetsMap[assetAddress];

      if (!debtAssetMetrics) {
        return {
          type: 'invalid',
          value: {
            bigNumber: BIG_ZERO,
            decimals,
          },
        };
      }

      const { remainingToBorrowTokens } = debtAssetMetrics;

      return {
        type: 'ltvLimit',
        value: {
          decimals,
          bigNumber: BigNumber.from(remainingToBorrowTokens),
        },
      };
    }

    if (getHasEffectiveCollateral(selectedAsset)) {
      return {
        type: 'invalid',
        value: {
          bigNumber: BIG_ZERO,
          decimals,
        },
      };
    }

    const collateralAssets = getCollateralAssets(decoratedSilo);
    const { debtAssetsMap } = calculateLTV({
      collateralAssets: collateralAssets.map(mapToCollateralAsset),
      debtAssets: [mapToDebtAsset(selectedAsset)],
    });

    const { remainingToBorrowTokens } = debtAssetsMap[selectedAsset.assetAddress];

    return {
      type: 'ltvLimit',
      value: {
        decimals,
        bigNumber: BigNumber.from(remainingToBorrowTokens),
      },
    };
  };

  const userMax = getUserMax();

  const liquidity: BigNumberWithDecimals = {
    bigNumber: selectedAsset.liquidity,
    decimals: selectedAsset.decimals,
  };

  if (liquidity.bigNumber.lt(userMax.value.bigNumber)) {
    return {
      type: 'liquidity',
      value: liquidity,
    };
  }

  return userMax;
};

const validateForm: ValidateForm = ({
  selectedAsset,
  normalizedTokenAmount,
  maxAmount,
  decoratedSilo,
  amountInUSD,
  protocolInstance,
  priceInUSD,
}) => {
  /**
   * This validation is not applicable for beta but may be in the future
   */
  if (getHasEffectiveCollateral(selectedAsset)) {
    return {
      message: 'You cannot borrow a token you have already deposited.',
    };
  }

  if (normalizedTokenAmount?.gt(selectedAsset.liquidity)) {
    return {
      message: `There's insufficient ${selectedAsset.symbol} in this silo to borrow`,
    };
  }

  if (normalizedTokenAmount && !decoratedSilo.hasPosition) {
    const disjoinedSymbols = pipe(decoratedSilo, getAvailableCollateralAssets, disjoinSymbols);

    return {
      message: `In order to borrow, please deposit some ${disjoinedSymbols}.`,
    };
  }

  if (normalizedTokenAmount?.gt(maxAmount.value.bigNumber)) {
    return pipe(
      decoratedSilo,
      getNonDebtAssets,
      filter(({ assetAddress }) => assetAddress !== selectedAsset.assetAddress),
      Either.of,
      Either.filterOrElse(
        (assets) => assets.length === 1,
        () => 'Insufficient collateral deposited.'
      ),
      Either.map((assets) => assets[0]),
      Either.map((asset) => `Insufficient ${asset.symbol} deposited.`),
      Either.toUnion,
      (message) => ({ message })
    );
  }

  const minBorrowAmount = getMinBorrowAmount(protocolInstance);

  if (amountInUSD && priceInUSD) {
    const { debtBalance, decimals } = selectedAsset;
    const debtBalanceUsd = convertCurrency({ bigNumber: debtBalance, decimals }, priceInUSD);
    const adjustedDebtBalance = adjustToFullUSD(debtBalanceUsd).bigNumber.toBigInt();
    const adjustedAmount = adjustToFullUSD(amountInUSD).bigNumber.toBigInt();

    if (adjustedDebtBalance + adjustedAmount < minBorrowAmount.value) {
      return {
        message: `Minimum borrow amount is ${asFullUSD(minBorrowAmount)}`,
      };
    }
  }

  return null;
};

const healthFactorConfig: HealthFactorConfig = {
  getTokenAdjustment: ({ action, tokens }) => {
    if (action !== 'borrow') return 0n;

    return tokens;
  },
  getMin: ({ riskFactor }) => riskFactor,
};

export type BorrowFormProps = {
  protocolInstance: ProtocolInstance;
} & (
  | {
      noDeposits: true;
      doBorrow?: never;
    }
  | {
      doBorrow: DoBorrow;
      noDeposits?: false | undefined;
    }
);

export const BorrowForm: FC<BorrowFormProps> = ({ protocolInstance, noDeposits, doBorrow }) => {
  const decoratedSilo = useThisDecoratedSilo();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const marketAddress = makeAddressSync(decoratedSilo.siloAddress);
  const hasUnstableOracle = getHasUnstableOracle({ protocolKey: protocolInstance, marketAddress });
  const routerSelectedAddress = useRouterSelectedAddress('borrow');
  const clearAssetFromQueryString = useClearAssetFromQueryString('borrow');
  const isActionFrozen = useIsAssetActionFrozen('borrow');

  const preChartPanels = noDeposits ? [] : [<LiquidationPriceSimulator />];

  const postChartPanels = [noDeposits ? <AssetBorrowInfo /> : <UserBorrowInfo />];

  const userHasLoan = getUserHasLoan(decoratedSilo);
  const showNewHealthFactor = !userHasLoan && !noDeposits;
  const assetsToDisplay = useMemo(() => {
    const siloConfig = getConfigurationForSilo(decoratedSilo.siloAddress);

    return getAssetsToDisplay(protocolInstance, siloConfig, decoratedSilo);
  }, [protocolInstance, decoratedSilo, getConfigurationForSilo]);

  return (
    <ActionForm
      protocolInstance={protocolInstance}
      getMaxAmount={getMaxAmount}
      queryStringSelectedAddress={routerSelectedAddress}
      maxLabelSlot={
        <div className="flex gap-2 items-center">
          <span>Available to borrow</span>
          <LiquidityTooltip action="borrow" />
        </div>
      }
      getNewLTV={getNewLTV}
      getNewUtilization={getNewUtilization}
      postMaxSlot={showNewHealthFactor ? <NewHealthFactor /> : undefined}
      submitButtonContentSlot={({ selectedAsset }) => (
        <SubmitButtonContent
          submitCopy={
            isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)
              ? `Borrowing ${selectedAsset.symbol} is temporarily disabled`
              : 'Borrow'
          }
          submittingCopy="Borrowing"
        />
      )}
      preChartPanels={preChartPanels}
      postChartPanels={postChartPanels}
      assetsToDisplay={assetsToDisplay}
      validateForm={validateForm}
      doSubmit={noDeposits ? () => Promise.resolve() : doBorrow}
      disableSubmit={({ selectedAsset }) => isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)}
      onSelectedAddressChange={clearAssetFromQueryString}
      data-cy="borrow-form"
      healthFactorConfig={healthFactorConfig}
      submissionModalContentSlot={hasUnstableOracle ? <UnstableOracleWarning /> : undefined}
      aprChartConfig={{
        mapAPYData,
        getAverageAPY,
      }}
      termsOfUseAction="borrow"
    />
  );
};

const getNewUtilization: GetNewUtilization = (amount, deposits, liquidity) => {
  return calculateUtilization(deposits, deposits - liquidity + amount);
};

const mapAPYData: MapAPYData = (data) => data.map((d) => ({ ...d, depositApy: undefined }));

const getAverageAPY: GetAverageAPY = (data) => mean(data.map((d) => d.borrowApy));

const NewHealthFactor: FC = () => {
  const { normalizedTokenAmount, setTokenAmount, selectedAsset, isPending } = useActionFormContext();
  const decoratedSilo = useThisDecoratedSilo();

  return (
    <HealthFactor
      variant="new"
      normalizedTokenAmount={normalizedTokenAmount}
      setTokenAmount={setTokenAmount}
      decoratedSilo={decoratedSilo}
      newDebtAsset={selectedAsset}
      disabled={isPending}
    />
  );
};
